import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { UserContext } from '../../UserContext';
import { useParams } from 'react-router-dom';

const API_BASE_URL = 'https://wholeness-forward.onrender.com/api';

const MeditationForum = () => {
  const { user } = useContext(UserContext);
  const { category } = useParams();
  const [threads, setThreads] = useState([]);
  const [newThreadTitle, setNewThreadTitle] = useState('');
  const [newThreadContent, setNewThreadContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [reactionLoading, setReactionLoading] = useState(false);
  const [error, setError] = useState('');
  

  const validReactions = ['like', 'love', 'peaceful', 'grateful', 'insightful'];

  // Fetch threads and their comments
  const fetchThreads = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/meditation/${category}/threads`);
      const threadData = response.data;
      await fetchComments(threadData); // Load comments for threads
    } catch (error) {
      console.error('Error fetching threads:', error);
      setError('Failed to load threads.');
    } finally {
      setLoading(false);
    }
  }, [category]);

  const fetchComments = async (threads) => {
    try {
      const updatedThreads = await Promise.all(
        threads.map(async (thread) => {
          const response = await axios.get(`${API_BASE_URL}/meditation/${thread._id}/comments`);
          const comments = Array.isArray(response.data) ? response.data : [];

          // Populate replies for comments
          const updatedComments = await Promise.all(
            comments.map(async (comment) => {
              const repliesResponse = await axios.get(
                `${API_BASE_URL}/meditation/${thread._id}/${comment._id}/replies`
              );
              comment.replies = Array.isArray(repliesResponse.data) ? repliesResponse.data : [];
              return comment;
            })
          );

          thread.comments = updatedComments;
          return thread;
        })
      );
      setThreads(updatedThreads);
    } catch (error) {
      console.error('Error fetching comments:', error);
      setError('Failed to load comments.');
    }
  };

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  const handlePostThread = async (e) => {
    e.preventDefault();
    if (!user || !user.token) {
      setError('You must be logged in to post a thread.');
      return;
    }

    if (!newThreadTitle.trim() || !newThreadContent.trim()) {
      setError('Title and content are required.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/meditation/${user.id}/${category}/threads`,
        { title: newThreadTitle, content: newThreadContent },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setThreads([response.data, ...threads]);
      setNewThreadTitle('');
      setNewThreadContent('');
    } catch (error) {
      console.error('Error posting thread:', error);
      setError('Failed to post thread.');
    } finally {
      setLoading(false);
    }
  };

  const handleReaction = async (type, id, level) => {
    if (!user || !user.token) {
      setError('You must be logged in to react.');
      return;
    }

    if (!validReactions.includes(type)) {
      setError('Invalid reaction type.');
      return;
    }

    setReactionLoading(true);
    setError('');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/meditation/${level}/${id}/reaction/${type}`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      const updatedReactions = response.data.reactions;
      setThreads((prevThreads) =>
        prevThreads.map((thread) => {
          if (level === 'thread' && thread._id === id) {
            return { ...thread, reactions: updatedReactions };
          }
          if (level === 'comment') {
            return {
              ...thread,
              comments: thread.comments.map((comment) =>
                comment._id === id ? { ...comment, reactions: updatedReactions } : comment
              ),
            };
          }
          if (level === 'reply') {
            return {
              ...thread,
              comments: thread.comments.map((comment) => ({
                ...comment,
                replies: comment.replies.map((reply) =>
                  reply._id === id ? { ...reply, reactions: updatedReactions } : reply
                ),
              })),
            };
          }
          return thread;
        })
      );
    } catch (error) {
      console.error('Error reacting:', error);
      setError('Failed to react.');
    } finally {
      setReactionLoading(false);
    }
  };

  return (
    <div className="meditation-forum">
      <h1>{category} Meditation Forum</h1>

      {error && <div className="error-message">{error}</div>}

      {user && (
        <div className="new-thread">
          <h2>Post a New Thread</h2>
          <form onSubmit={handlePostThread}>
            <input
              type="text"
              placeholder="Title"
              value={newThreadTitle}
              onChange={(e) => setNewThreadTitle(e.target.value)}
            />
            <textarea
              placeholder="Share your thoughts"
              value={newThreadContent}
              onChange={(e) => setNewThreadContent(e.target.value)}
            />
            <button type="submit" disabled={loading}>
              {loading ? 'Posting...' : 'Post'}
            </button>
          </form>
        </div>
      )}

      <div className="threads">
        <h2>Recent Threads</h2>
        {loading ? (
          <p>Loading threads...</p>
        ) : (
          threads.map((thread) => (
            <div key={thread._id} className="thread">
              <h3>{thread.title}</h3>
              <p>{thread.content}</p>
              <small>Posted by: {thread.username}</small>
              <div className="reactions">
                {validReactions.map((reaction) => (
                  <button
                    key={reaction}
                    onClick={() => handleReaction(reaction, thread._id, 'thread')}
                    disabled={reactionLoading}
                  >
                    {reaction} ({thread.reactions?.[reaction] || 0})
                  </button>
                ))}
              </div>
              {/* Additional comments and replies UI as shown in your example */}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default MeditationForum;
